import { useCallback, useEffect, useRef, useState } from 'react'
import { reilmAPI } from '../../../services/apiClient'
import { useAuth } from '../../auth/AuthContext'
import CloseIconButton, { DARK_APPEARANCE } from '../../../components/CloseIconButton'
import TemplateListModal from './TemplateListModal'
import StartAnalysisModal from './StartAnalysisModal'
import OverwriteConfirmModal from './OverwriteConfirmModal'
import DeleteConfirmModal from './DeleteConfirmModal'
import DeleteBlockedModal from './DeleteBlockedModal'
import { isHandledElsewhere, ScenarioBusyError, UnsavedChangesError } from '../requestErrors'
import { missesStarterType, nextTemplateName, defaultCheckedIds, pruneCheckedIds, DRAFT_TEMPLATE_ID } from './templateFields'

// One state machine for the template pop-ups: Manage Templates (`manage`, the header's Templates button;
// TemplateListModal plus the confirm/notice pop-ups) and "Start your analysis" (otherwise;
// StartAnalysisModal). Flip.jsx passes `onImport` (create scenarios from these template ids, returns the
// request's promise), `saveSource` (the saved scenario Update Template / Save as New Template copy, or
// null) and `onBeforeSave` (flushes its autosave). `preselectFirst` checks the top row (letter 99, 2.9;
// letter 106 D2). README.md carries the backend contract.
const SAVE_TYPE_ERRORS = {
  loan: 'This loan scenario can only update a loan template.',
  cash: 'This cash scenario can only update a cash template.',
}
const typeOf = (value) => (typeof value === 'string' && value.toLowerCase() === 'loan' ? 'loan' : 'cash')

export default function TemplatesFlow({ open, onClose, scenario, onImport, saveSource = null, onBeforeSave, preselectFirst = false, manage = false }) {
  const { user } = useAuth()
  const [templates, setTemplates] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [checkedIds, setCheckedIds] = useState(new Set())
  const [view, setView] = useState('select')
  const [editingId, setEditingId] = useState(null)
  const [editValue, setEditValue] = useState('')
  const [popup, setPopup] = useState(null)
  const [activeId, setActiveId] = useState(null)
  const [blockedType, setBlockedType] = useState('')
  const [busy, setBusy] = useState(false)
  // While Import is running the popup cannot be closed, so its result or error always has a place to show.
  const [importing, setImporting] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [popupError, setPopupError] = useState(null)
  // The save-errors inline state (the scenario's type, which picks the drawn sentence), or null.
  const [saveTypeError, setSaveTypeError] = useState(null)
  const loadingBackdropRef = useRef(null)

  // Must be defined before the early returns below, and memoized -- see README.md ("closeAll's
  // placement and memoization") for why.
  const closeAll = useCallback(() => {
    onClose()
    setView('select')
    setEditingId(null)
    setPopup(null)
    setActionError(null)
    setPopupError(null)
    setSaveTypeError(null)
  }, [onClose])

  // Every fetch, a refetch of an incomplete list included, goes through the loading panel: nothing on
  // a stale list stays clickable, and a failure gets the error and Try again like the first load.
  const fetchTemplates = () => {
    setLoadError(null)
    setTemplates(null)
    reilmAPI
      .getFlipTemplates()
      .then(({ data }) => {
        // The server seeds any missing starter template (Cash, Hard Money) inside this GET (decision
        // D12); an empty or one-type list here means that seed write failed.
        const list = data?.data?.flipTemplate ?? []
        setTemplates(list)
        setCheckedIds(defaultCheckedIds(list, preselectFirst))
      })
      .catch(() => setLoadError("We couldn't load your templates. Please try again."))
  }

  // Lazy-loaded on first open, not on every mount -- Flip.jsx mounts this unconditionally so its
  // own state (the loaded list) survives the modal being closed and reopened via the header
  // button. A list missing a type (the server's seed failed, D12) is fetched again on the next
  // open, since the seed is retried by that same GET. `loadError` blocks the automatic retry a
  // repeated open would otherwise cause, leaving the Try Again button as the explicit way out.
  // Every open starts from its own default selection, never the last open's (letter 99, 2.9); so does
  // a switch of pop-up while open (Manage open when deleting the last scenario turns it into Start).
  useEffect(() => {
    if (!open) return
    setActionError(null)
    if ((templates === null || missesStarterType(templates)) && !loadError) fetchTemplates()
    else if (templates !== null) setCheckedIds(defaultCheckedIds(templates, preselectFirst))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, manage])

  // Escape for the pending/failed-fetch panel only (templates === null): before this, an auto-
  // opened modal stuck on a slow or failing fetch had no keyboard exit at all. Scoped this way
  // rather than document-wide so it can never double-fire with TemplateRow's own rename-input
  // Escape, which only exists once templates have loaded.
  useEffect(() => {
    if (!open || templates !== null) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeAll()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, templates, closeAll])

  if (!open) return null

  if (templates === null) {
    return (
      <div
        ref={loadingBackdropRef}
        className="fixed inset-0 z-[150] bg-black/50 flex items-center justify-center"
        // Same backdrop-close convention as TemplateListModal.jsx below (mousedown + target
        // identity), and the same reason: nothing else in this panel is draggable/selectable text,
        // but there is no reason to diverge from the resolved-list state's own pattern.
        onMouseDown={(e) => {
          if (e.target === loadingBackdropRef.current) closeAll()
        }}
      >
        {/* pt-[56px] clears the absolute close button vertically -- see README.md ("what has no
            design reference at all"). */}
        <div className="relative rounded-[16px] bg-[#F4FAEF] px-10 pt-[56px] pb-8 text-[14px] text-brand-dark flex flex-col items-center gap-y-[12px]">
          <CloseIconButton onClick={closeAll} ariaLabel="Close" appearance={DARK_APPEARANCE} className="absolute top-[16px] right-[16px]" />
          <p>{loadError || 'Loading templates…'}</p>
          {loadError && (
            <button
              type="button"
              onClick={fetchTemplates}
              className="px-[20px] py-[8px] rounded-[48px] bg-[#4AA52A] hover:bg-[#328C12] text-white text-[13px] font-semibold"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    )
  }

  const activeTemplate = templates.find((t) => t._id === activeId) ?? null

  // After Save and after Delete return to the list state, not the whole flow (mr-6 spec S3.4/S8.3,
  // S15 decision 8).
  const toReturnToList = () => {
    setPopup(null)
    setPopupError(null)
    setView('select')
    setEditingId(null)
  }

  // Manage Templates selects one template, the one Update Template overwrites (letter 106 R4a, R4b).
  const handleToggleChecked = (id) => {
    // Start's rows stay put while its request runs, so the label keeps counting what is being created.
    if (importing) return
    setSaveTypeError(null)
    if (manage) return setCheckedIds(new Set([id]))
    setCheckedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // One scenario per checked template, in list order rather than click order (decision D1); the
  // server copies names, types and values. The popup stays open on a failure, with the reason.
  const handleImport = () => {
    if (busy) return
    const templateIds = templates.filter((t) => checkedIds.has(t._id)).map((t) => t._id)
    if (templateIds.length === 0) return
    setBusy(true)
    setImporting(true)
    setActionError(null)
    onImport(templateIds)
      .then(() => {
        setImporting(false)
        closeAll()
      })
      .catch((err) => {
        setImporting(false)
        if (err instanceof ScenarioBusyError) return setActionError(err.message)
        if (isHandledElsewhere(err)) return
        const error = err?.response?.data?.error
        // No design for the limit (spec §3.3, D2): our own short line in the popup's error style.
        if (error?.code === 'FLIP_SCENARIO_LIMIT') setActionError('This property has the maximum number of scenarios. Delete one to import more.')
        else setActionError(error?.message || "We couldn't add the scenarios. Please try again.")
      })
      .finally(() => setBusy(false))
  }

  const closeUnlessImporting = () => {
    if (!importing) closeAll()
  }

  // Not mid-request: the request's own return to the list would drop what this starts.
  const handleStartEdit = (template) => {
    if (busy) return
    setView('rename')
    setEditingId(template._id)
    setEditValue(template.template_name)
    setActionError(null)
  }

  const handleCancelEdit = () => {
    // Mid-request the draft's Save has already been sent: Cancel would hide the row, then the
    // template would arrive anyway (letter 99, 2.5: Cancel adds nothing).
    if (busy) return
    setView('select')
    setEditingId(null)
    setEditValue('')
    setActionError(null)
  }

  const replaceTemplate = (updated) => {
    if (updated?._id) setTemplates((prev) => prev.map((t) => (t._id === updated._id ? updated : t)))
  }

  // The rename panel's Save renames only; the Overwrite popup belongs to the list state's Save
  // (README.md, "Save and rename are two actions").
  const handleCommitEdit = () => {
    if (busy) return
    // Save has no disabled state (letter 106 R1), so an empty name is answered here.
    if (!editValue.trim()) return setActionError('Enter a template name.')
    if (editingId === DRAFT_TEMPLATE_ID) {
      return saveSource ? createTemplate(editValue.trim()) : setActionError('Open a saved scenario to save it as a new template.')
    }
    setBusy(true)
    setActionError(null)
    reilmAPI
      .updateFlipTemplate(editingId, { template_name: editValue.trim() })
      .then(({ data }) => {
        replaceTemplate(data?.data?.template)
        toReturnToList()
      })
      .catch((err) => {
        if (isHandledElsewhere(err)) return
        setActionError(err?.response?.data?.error?.message || "We couldn't rename the template. Please try again.")
      })
      .finally(() => setBusy(false))
  }

  // List-state Save (D19, D20): exactly one checked template of the scenario's own type, else the
  // drawn save-errors sentence and no request.
  const handleRequestSave = () => {
    if (busy || !saveSource || checkedIds.size !== 1) return
    const template = templates.find((t) => checkedIds.has(t._id))
    if (!template) return
    setActionError(null)
    const scenarioType = typeOf(scenario?.scenarioType)
    if (typeOf(template.template_type) !== scenarioType) {
      setSaveTypeError(scenarioType)
      return
    }
    setSaveTypeError(null)
    setActiveId(template._id)
    setPopup('overwrite')
    setPopupError(null)
  }

  // The server copies the saved scenario (D21), so the pending autosave goes first. Success returns
  // to the list with the template's new Last Modified (no drawn success state).
  const handleConfirmOverwrite = () => {
    if (busy || !activeTemplate || !saveSource) return
    setBusy(true)
    setPopupError(null)
    Promise.resolve(onBeforeSave?.())
      .then(() => reilmAPI.saveFlipTemplateValues(activeTemplate._id, saveSource))
      .then(({ data }) => {
        replaceTemplate(data?.data?.template)
        toReturnToList()
      })
      .catch((err) => {
        if (err instanceof UnsavedChangesError) return setPopupError(err.message)
        if (isHandledElsewhere(err)) return
        const error = err?.response?.data?.error
        if (error?.code === 'FLIP_TEMPLATE_TYPE_MISMATCH') {
          toReturnToList()
          setSaveTypeError(typeOf(scenario?.scenarioType))
          return
        }
        setPopupError(error?.message || "We couldn't save your changes. Please try again.")
      })
      .finally(() => setBusy(false))
  }

  const handleRequestDelete = (template) => {
    if (busy) return
    setActiveId(template._id)
    setPopup('delete-confirm')
    setPopupError(null)
  }

  // The disabled trash icon (RowIconCluster) already blocks the obvious path client-side, but the
  // DELETE call still goes through and the server's own floor guard is what this branch reacts to
  // (mr-6 spec S15 decision 6) -- a stale client list or a second tab is enough to reach this even
  // with the icon disabled everywhere the client can currently see. The 409's own `details.template_type`
  // is read for the blocked-popup copy rather than the locally cached row, since that race is exactly
  // why this branch exists -- the local row's type is the thing that may be stale.
  const handleConfirmDelete = () => {
    if (busy || !activeTemplate) return
    setBusy(true)
    setPopupError(null)
    reilmAPI
      .deleteFlipTemplate(activeTemplate._id)
      .then(({ data }) => {
        const nextList = data?.data?.flipTemplate ?? templates.filter((t) => t._id !== activeTemplate._id)
        setTemplates(nextList)
        // A selected row that no longer exists would leave Update Template aimed at nothing: prune it
        // (fix round 23.09.2026).
        setCheckedIds((prev) => pruneCheckedIds(prev, nextList))
        toReturnToList()
      })
      .catch((err) => {
        const error = err?.response?.data?.error
        if (error?.code === 'TEMPLATE_TYPE_FLOOR') {
          setBlockedType(error?.details?.template_type ?? activeTemplate.template_type ?? '')
          setPopup('delete-blocked')
          reconcileAfterBlockedDelete()
        } else {
          setPopupError(error?.message || "We couldn't delete the template. Please try again.")
        }
      })
      .finally(() => setBusy(false))
  }

  // A 409 here means this tab's own list has already diverged from the server (another tab, or a
  // second delete, won the race) -- re-fetch so the phantom row a stale list still shows stops
  // being checkable/importable (fix round 23.09.2026). The popup itself already carries the
  // server's own correct copy regardless of this call's outcome.
  const reconcileAfterBlockedDelete = () => {
    reilmAPI
      .getFlipTemplates()
      .then(({ data }) => {
        const list = data?.data?.flipTemplate
        if (!list) return
        setTemplates(list)
        setCheckedIds((prev) => pruneCheckedIds(prev, list))
      })
      .catch(() => setActionError('Templates list may be out of date. Please refresh.'))
  }

  // Save as New Template copies the saved scenario on screen (D22), after its pending autosave; a
  // draft row takes the name first (letter 99, 2.5; letter 106 R4d).
  const handleCreateNew = () => {
    if (busy) return
    setActionError(null)
    setSaveTypeError(null)
    if (!saveSource) return
    const name = nextTemplateName(templates.map((t) => t.template_name))
    setView('rename')
    setEditingId(DRAFT_TEMPLATE_ID)
    setEditValue(name)
  }

  const createTemplate = (name) => {
    setBusy(true)
    // The draft's create holds the pop-up open like Import, so its result never lands after a close.
    setImporting(true)
    setActionError(null)
    // Type derivation: no frame or comment specifies it -- decided here from the current scenario's
    // own scenarioType (Property.flipAnalysis's field, distinct from the template's own
    // template_type), so a Cash analysis seeds a Cash template and a Loan analysis seeds a Loan
    // one, rather than a fixed guess (README.md, "New Template's type"). Lowercase to match
    // FLIP_TEMPLATE_TYPES (reilm-be/modules/users/flipTemplateFields.js).
    const type = scenario?.scenarioType === 'loan' ? 'loan' : 'cash'
    Promise.resolve(onBeforeSave?.())
      .then(() => reilmAPI.createFlipTemplate({ template_name: name, template_type: type, ...saveSource }))
      .then(({ data }) => {
        const created = data?.data?.template
        if (!created) return
        setTemplates((prev) => [...prev, created])
        toReturnToList()
      })
      .catch((err) => {
        if (err instanceof UnsavedChangesError) return setActionError(err.message)
        if (isHandledElsewhere(err)) return
        setActionError(err?.response?.data?.error?.message || "We couldn't create the template. Please try again.")
      })
      .finally(() => {
        setBusy(false)
        setImporting(false)
      })
  }

  // Directional, one step at a time -- the server computes the swap and returns the full resulting
  // list, so this trusts that response as truth rather than swapping locally first (README.md,
  // "reorder shape"). A boundary move the server treats as a no-op still comes back 200 with the
  // list unchanged, which this applies the same as any other successful response.
  const handleMove = (id, direction) => {
    if (busy) return
    setBusy(true)
    setActionError(null)
    reilmAPI
      .reorderFlipTemplate(id, direction)
      .then(({ data }) => {
        const list = data?.data?.flipTemplate
        if (!list) return
        setTemplates(list)
        // A selected id deleted in another tab must not survive a reorder (fix round 23.09.2026).
        setCheckedIds((prev) => pruneCheckedIds(prev, list))
      })
      .catch(() => {
        setActionError("We couldn't reorder the templates. Please try again.")
      })
      .finally(() => setBusy(false))
  }

  if (!manage) {
    return (
      <StartAnalysisModal
        templates={templates}
        checkedIds={checkedIds}
        onToggleChecked={handleToggleChecked}
        onStart={handleImport}
        onClose={closeUnlessImporting}
        busy={busy}
        error={actionError}
      />
    )
  }

  return (
    <>
      {popup === null && (
        <TemplateListModal
          templates={templates}
          checkedIds={checkedIds}
          onToggleChecked={handleToggleChecked}
          onSave={handleRequestSave}
          canSave={Boolean(saveSource)}
          saveError={saveTypeError ? SAVE_TYPE_ERRORS[saveTypeError] : null}
          view={view}
          editingId={editingId}
          editValue={editValue}
          onEditValueChange={setEditValue}
          onStartEdit={handleStartEdit}
          onCancelEdit={handleCancelEdit}
          onCommitEdit={handleCommitEdit}
          onCreateNew={handleCreateNew}
          onRequestDelete={handleRequestDelete}
          onMoveUp={(id) => handleMove(id, 'up')}
          onMoveDown={(id) => handleMove(id, 'down')}
          onClose={closeUnlessImporting}
          busy={busy}
          error={actionError}
        />
      )}
      <OverwriteConfirmModal
        open={popup === 'overwrite'}
        templateName={activeTemplate?.template_name ?? ''}
        onConfirm={handleConfirmOverwrite}
        onClose={toReturnToList}
        isLoading={busy}
        error={popupError}
      />
      <DeleteConfirmModal
        open={popup === 'delete-confirm'}
        templateName={activeTemplate?.template_name ?? ''}
        onConfirm={handleConfirmDelete}
        onClose={toReturnToList}
        isLoading={busy}
        error={popupError}
      />
      <DeleteBlockedModal
        open={popup === 'delete-blocked'}
        templateType={blockedType}
        user={user}
        onClose={toReturnToList}
      />
    </>
  )
}
